<template>
  <main-large-dialog
    :is-visible.sync="computedIsVisible"
    :is-valid="isFormValid"
    :title="title"
    min-body-height="55vh"
    dialog-width="60%"
    header-shadow
    :agree-text="agreeText"
    @agree="handleAction"
    no-paddings-x
    :loading="isActionLoading"
  >
    <template #body>
      <v-col cols="12">
        <v-form ref="reportForm" v-model="isFormValid">
          <template v-if="isLoaded">
            <v-row v-for="(input, index) in inputs" :key="index" no-gutters class="border-bottom pb-4" align="center">
              <v-col cols="3" class="text-left pb-5 pl-7">
                <span> {{ $i18n.t(input.translationPath) }}: </span>
              </v-col>
              <v-col style="max-width: 275px !important; margin-left: 50px">
                <data-input v-model="computedReport[input.name]" v-bind="input.props" />
              </v-col>
            </v-row>
            <v-row no-gutters class="px-7">
              <v-col cols="12">
                <v-row no-gutters class="pb-2">
                  <v-col cols="auto">
                    <span>
                      {{ $i18n.t('admin.customReports.SQLQuery') }}
                    </span>
                  </v-col>
                  <v-spacer></v-spacer>
                  <v-col v-if="!isNew" cols="auto">
                    <base-button
                      translation-path="admin.customReports.validateQuery"
                      class="mr-0"
                      :disabled="!computedReport.sql_query"
                      :loading="isValidateLoading"
                      @click="validateQueryHandler"
                    ></base-button>
                  </v-col>
                </v-row>
                <v-row no-gutters>
                  <v-col>
                    <data-input
                      v-model="computedReport.sql_query"
                      :data-type="{ name: 'textarea' }"
                      :rules="[v => !!v || $i18n.t('rules.required')]"
                    />
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
          </template>
        </v-form>
      </v-col>
    </template>
  </main-large-dialog>
</template>

<script>
import DataInput from '@/components/DataInput';
import { get, call } from 'vuex-pathify';

export default {
  name: 'ZDMReportsTabCustomDialog',
  components: {
    MainLargeDialog: () => import('@/components/MainLargeDialog'),
    DataInput,
  },
  props: {
    isVisible: {
      type: Boolean,
      required: true,
    },
    reportData: {
      type: Object,
    },
    isZdm: {
      type: Boolean,
      default: false,
    },
    reportGroups: {
      type: Array,
      default: () => [],
    },
  },
  data: () => ({
    isActionLoading: false,
    isFormValid: false,
    isLoaded: true,
    isValidateLoading: false,
  }),
  computed: {
    layers: get('layers/layers'),
    computedReportGroups() {
      return this.reportGroups.map((report, index) => {
        return {
          ...report,
          order: index + 1,
        };
      });
    },
    featuresLayers() {
      return this.$_objectToArray(this.layers).filter(layer =>
        layer.type === 'features_layer' && layer.layer_scope === this.isZdm ? 'zdm_data' : 'core'
      );
    },
    inputs() {
      return [
        {
          translationPath: 'admin.customReports.reportName',
          name: 'name',
          props: {
            dataType: {
              name: 'text',
            },
            rules: [v => !!v || this.$i18n.t('rules.required')],
          },
        },
        {
          translationPath: 'admin.customReports.reportLayer',
          name: this.isZdm ? 'layer' : 'layer_id',
          props: {
            dataType: {
              name: 'select',
            },
            rules: [v => !!v || this.$i18n.t('rules.required')],
            items: this.featuresLayers,
            itemText: 'name',
            itemValue: 'id',
          },
        },
        ...(!this.isZdm
          ? [
              {
                translationPath: 'admin.customReports.reportGroup',
                name: 'group_id',
                props: {
                  dataType: {
                    name: 'select',
                  },
                  rules: [v => !!v || this.$i18n.t('rules.required')],
                  items: this.computedReportGroups,
                  itemText: 'name',
                  itemValue: 'id',
                },
              },
            ]
          : []),
        {
          translationPath: 'admin.customReports.reportDescription',
          name: 'description',
          props: {
            dataType: {
              name: 'text',
            },
          },
        },
      ];
    },
    agreeText() {
      return this.isNew ? 'dialog.agreeAdd' : 'dialog.agreeSave';
    },
    isNew() {
      return this.reportData ? false : true;
    },
    title() {
      return this.isNew
        ? this.$i18n.t('admin.customReports.addReport')
        : this.$i18n.t('admin.customReports.editReport');
    },
    computedIsVisible: {
      get() {
        return this.isVisible;
      },
      set(nV) {
        this.$emit('update:isVisible', nV);
      },
    },
    computedReport() {
      const payloadKeys = [
        'name',
        'description',
        'sql_query',
        ...(this.isZdm ? ['layer'] : ['layer_id', 'group_id', 'group_order']),
      ];
      let newReportObject = payloadKeys.reduce((acc, key) => {
        acc[key] = this.isNew ? null : this.reportData[key];
        return acc;
      }, {});
      return newReportObject;
    },
  },
  watch: {
    isVisible(nV) {
      if (nV) {
        if (this.isNew) this.$refs?.reportForm?.reset();
        this.$refs?.reportForm?.resetValidation();
      }
    },
  },
  methods: {
    addCustomReportHandler: call('customReports/addCustomReport'),
    editCustomReportHandler: call('customReports/editCustomReport'),

    addZdmCustomReportHandler: call('zdmCustomReports/addCustomReport'),
    editZdmCustomReportHandler: call('zdmCustomReports/editCustomReport'),
    zdmValidateQuery: call('zdmCustomReports/validateQuery'),
    validateQuery: call('customReports/validateQuery'),
    addCustomReport(payload) {
      return this.isZdm ? this.addZdmCustomReportHandler(payload) : this.addCustomReportHandler(payload);
    },
    editCustomReport(payload) {
      return this.isZdm ? this.editZdmCustomReportHandler(payload) : this.editCustomReportHandler(payload);
    },
    getPayload() {
      let payloadCopy = JSON.parse(JSON.stringify(this.computedReport));
      if (!this.isZdm && !payloadCopy.group_order) {
        payloadCopy.group_order = 1;
      }
      if (!this.isZdm && payloadCopy.name === this.reportData?.name) delete payloadCopy.name;
      return payloadCopy;
    },
    async validateQueryHandler() {
      this.isValidateLoading = true;
      try {
        this.isZdm ? await this.zdmValidateQuery(this.reportData.id) : await this.validateQuery(this.reportData.id);
        this.$store.set('snackbar/PUSH_MESSAGE!', {
          message: this.$i18n.t('dialog.sqlQueryValid'),
        });
      } catch (error) {
        console.log('error', error);
      } finally {
        this.isValidateLoading = false;
      }
    },
    async handleAction() {
      this.isActionLoading = true;
      try {
        if (this.isNew) {
          await this.addCustomReport(this.getPayload());
        } else {
          await this.editCustomReport({
            id: this.reportData.id,
            data: this.getPayload(),
          });
        }
        this.computedIsVisible = false;
      } finally {
        this.isActionLoading = false;
      }
    },
  },
};
</script>
